import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  MatMenuTrigger,
  MatMenu,
  MatMenuItem,
  MatMenuContent
} from '@angular/material/menu';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort, MatSortHeader } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AppStateService } from '@pcplus/core';
import {
  debounceTime,
  of,
  ReplaySubject,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { AdditionalInfo } from '../../dto/additional-info';
import { ColumnDefinition } from '../../dto/column-definition';
import { QueryResults } from '../../dto/query-results';
import { SearchParameter } from '../../dto/search-parameter';
import { TypeCode } from '../../dto/type-code';
import { DateFilterComponent } from '../filter/date-filter/date-filter.component';
import { FilterInput } from '../filter/filter-input';
import { NumericFilterComponent } from '../filter/numeric-filter/numeric-filter.component';
import { RangeItems } from '../filter/range-items';
import { TextFilterComponent } from '../filter/text-filter/text-filter.component';
import { NgIf, NgFor, DatePipe } from '@angular/common';
import {
  MatCard,
  MatCardHeader,
  MatCardAvatar,
  MatCardTitle,
  MatCardSubtitle
} from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatBadge } from '@angular/material/badge';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import {
  MatMiniFabButton,
  MatIconButton,
  MatFabButton
} from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HeaderItemLocation } from '../header/header-item-location';
import { InputDialogComponent } from '../input-dialog/input-dialog.component';
import { Directionality } from '@angular/cdk/bidi';
import { MatTooltip } from '@angular/material/tooltip';
import { HeaderInjectDirective } from '../header/header-inject.directive';

@Component({
  selector: 'pcp-data-display',
  templateUrl: './data-display.component.html',
  styleUrls: ['./data-display.component.scss'],
  imports: [
    NgIf,
    NgFor,
    MatCard,
    MatCardHeader,
    MatCardAvatar,
    MatIcon,
    MatCardTitle,
    MatCardSubtitle,
    HeaderInjectDirective,
    MatSort,
    MatSortHeader,
    MatBadge,
    MatTableModule,
    MatToolbar,
    MatToolbarRow,
    MatTooltip,
    MatPaginator,
    MatMiniFabButton,
    MatFabButton,
    MatMenuTrigger,
    MatProgressSpinner,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuContent,
    DatePipe
  ]
})
export class DataDisplayComponent implements OnInit, AfterViewInit {
  constructor(
    public _liveAnnouncer: LiveAnnouncer,
    public state: AppStateService,
    public renderer: Renderer2,
    private dir: Directionality,
    public dialog: MatDialog
  ) {}

  pressed = false;
  currentResizeIndex = 0;
  startX = 0;
  startWidth = 130;
  resizableMousemove: () => void = () => {};
  resizableMouseup: () => void = () => {};

  @Input() getData = (
    _sort: MatSort | null,
    _page: number,
    _count: number,
    _search: Array<SearchParameter>
  ) => of(new QueryResults<unknown>([], 0));
  @Input() downloadPage = () => of(true);
  @Input() downloadAll = () => of(true);
  @Input() addItem = () => of(true);

  @Input() title = '';
  moving = false;
  @Input() showGoBack = false;
  @Input() showExport = true;
  @Input() showAddButton = false;
  @Input() addButtonTooltip = '';
  @Input() icon = 'local_shipping';
  @Input() fontSet: string | null = null;
  search = new Array<SearchParameter>();
  @Input() data: QueryResults | null = null;
  @Output() dataChange = new EventEmitter<QueryResults | null>();
  @Input() colDefinitions = new Array<ColumnDefinition>();
  @Input() additionalInfo: AdditionalInfo[] | null = null;
  minDates = new RangeItems<Date>({});
  maxDates = new RangeItems<Date>({});
  minNumbers = new RangeItems<number>({});
  maxNumbers = new RangeItems<number>({});
  displayedColumns: string[] = [];
  dataSource: MatTableDataSource<unknown> | null = null;
  onDestroy$ = new Subject<void>();
  refresh$ = new ReplaySubject<void>(1);
  @ViewChild(MatSort) sort: MatSort | null = null;
  updating = false;
  customer: string | null = null;
  headerItemLocation = HeaderItemLocation;
  page = 0;
  count = 50;

  @ViewChild('filterMenu', { read: MatMenuTrigger })
  contextMenu: MatMenuTrigger | null = null;
  contextMenuPosition = { x: '0px', y: '0px' };

  ngAfterViewInit() {
    this.state.updateCurrentModuleName(this.title);
  }

  goBack() {
    history.back();
    return false;
  }

  isFiltered(column: string) {
    const srch = 'search' + column.toLowerCase();
    return this.search.some((s) => s.name.toLowerCase().includes(srch));
  }

  ngOnInit(): void {
    this.displayedColumns = this.colDefinitions.map((c) => c.name);
    this.refresh$
      .pipe(
        takeUntil(this.onDestroy$),
        debounceTime(300),
        tap(() => (this.updating = true)),
        switchMap(() =>
          this.getData(this.sort, this.page, this.count, this.search)
        ),
        shareReplay(1),
        tap(() => (this.updating = false))
      )
      .subscribe((d) => {
        if (!d) {
          d = new QueryResults([], 0);
        }
        this.data = d;
        this.dataChange.emit(this.data);
        if (d.additionalInfo) {
          this.colDefinitions.forEach((col) => {
            const capitalizedColName = this.capitalizeFirstLetter(col.name);
            switch (col.type) {
              case TypeCode.DateTime:
                this.getMinMaxDate(
                  d,
                  col.name,
                  'min' + capitalizedColName,
                  true
                );
                this.getMinMaxDate(
                  d,
                  col.name,
                  'max' + capitalizedColName,
                  false
                );
                break;
              case TypeCode.Decimal:
              case TypeCode.Double:
              case TypeCode.Int16:
              case TypeCode.Int32:
              case TypeCode.Int64:
                this.getMinMaxNumber(
                  d,
                  col.name,
                  'min' + capitalizedColName,
                  true
                );
                this.getMinMaxNumber(
                  d,
                  col.name,
                  'max' + capitalizedColName,
                  false
                );
                break;
            }
          });
        }
        this.refreshData(this.data.results);
      });
      this.refresh$.next();
  }

  private getMinMaxDate(
    d: QueryResults,
    name: string,
    minmax: string,
    isMin: boolean
  ) {
    const additional = d.additionalInfo![minmax];
    if (additional !== undefined) {
      const additionalDate = new Date(additional);
      const foundDate = isMin
        ? this.minDates.items[name]
        : this.maxDates.items[name];
      if (foundDate !== undefined) {
        if (isMin) {
          this.minDates.items[name] = new Date(
            Math.min(additionalDate.valueOf(), foundDate.valueOf())
          );
        } else {
          this.maxDates.items[name] = new Date(
            Math.max(additionalDate.valueOf(), foundDate.valueOf())
          );
        }
      } else {
        if (isMin) {
          this.minDates.items[name] = additionalDate;
        } else {
          this.maxDates.items[name] = additionalDate;
        }
      }
    }
  }

  private getMinMaxNumber(
    d: QueryResults,
    name: string,
    minmax: string,
    isMin: boolean
  ) {
    const additional = +d.additionalInfo![minmax];
    if (additional !== undefined) {
      const found = isMin
        ? this.minNumbers.items[name]
        : this.maxNumbers.items[name];
      if (found !== undefined) {
        if (isMin) {
          this.minNumbers.items[name] = Math.min(additional, found);
        } else {
          this.maxNumbers.items[name] = Math.max(additional, found);
        }
      } else {
        if (isMin) {
          this.minNumbers.items[name] = additional;
        } else {
          this.maxNumbers.items[name] = additional;
        }
      }
    }
  }

  refreshData(data: unknown[] | null) {
    if (data !== null && data.length > 0) {
      this.dataSource = new MatTableDataSource(data);
    } else {
      if (this.search.length > 0) {
        this.dataSource = new MatTableDataSource();
      } else {
        this.dataSource = null;
      }
    }
  }

  paging(e: PageEvent) {
    this.page = e.pageIndex;
    this.count = e.pageSize;
    this.refresh$.next();
  }

  /** Announce the change in sort state for assistive technology. */
  async announceSortChange(sortState: Sort) {
    // This example uses English messages. If your application supports
    // multiple language, you would internationalize these strings.
    // Furthermore, you can customize the message to add additional
    // details about the values being sorted.
    if (sortState.direction) {
      await this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      await this._liveAnnouncer.announce('Sorting cleared');
    }
    this.refresh$.next();
  }

  onContextMenu(event: MouseEvent, item: ColumnDefinition) {
    event.preventDefault();
    if (!this.contextMenu) {
      return;
    }
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.contextMenu.menuData = { item };
    this.contextMenu.menu?.focusFirstItem('mouse');
    this.contextMenu.openMenu();
  }

  capitalizeFirstLetter(name: string) {
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  filter(col: ColumnDefinition) {
    const capitalizedColName = this.capitalizeFirstLetter(col.name);
    switch (col.type) {
      case TypeCode.DateTime:
        {
          const currentDate = new Date();
          const totalMin = new Date(
            currentDate.setFullYear(currentDate.getFullYear() - 5)
          );
          const totalMax = new Date(
            currentDate.setFullYear(currentDate.getFullYear() + 5)
          );
          const minDate =
            this.minDates.items[col.name] !== undefined
              ? this.minDates.items[col.name]
              : totalMin;
          const maxDate =
            this.maxDates.items[col.name] !== undefined
              ? this.maxDates.items[col.name]
              : totalMax;
          const sp = this.search.find(
            (s) => s.name === 'search' + capitalizedColName
          );
          const tmpFrom = sp?.values[0];
          const curFrom = tmpFrom !== undefined ? new Date(tmpFrom) : minDate;
          const tmpTo = sp?.values[1];
          const curTo = tmpTo !== undefined ? new Date(tmpTo) : maxDate;
          this.dialog
            .open(DateFilterComponent, {
              direction: this.dir.value,
              data: new FilterInput<Date>(minDate, maxDate, curFrom, curTo, col)
            })
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result: SearchParameter) => {
              if (result) {
                this.updateSearch(result);
              }
            });
        }
        break;
      case TypeCode.Decimal:
      case TypeCode.Double:
      case TypeCode.Int16:
      case TypeCode.Int32:
      case TypeCode.Int64:
        {
          const minNum = this.minNumbers.items[col.name];
          const maxNum = this.maxNumbers.items[col.name];
          const numSp = this.search.find(
            (s) => s.name === 'search' + capitalizedColName
          );
          const tmpNumFrom = numSp?.values[0];
          const curNumFrom = tmpNumFrom !== undefined ? +tmpNumFrom : minNum;
          const tmpNumTo = numSp?.values[1];
          const curNumTo = tmpNumTo !== undefined ? +tmpNumTo : maxNum;
          this.dialog
            .open(NumericFilterComponent, {
              direction: this.dir.value,
              data: new FilterInput<number>(
                minNum,
                maxNum,
                curNumFrom,
                curNumTo,
                col
              )
            })
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result: SearchParameter) => {
              if (result) {
                this.updateSearch(result);
              }
            });
        }
        break;
      default:
        this.dialog
          .open(TextFilterComponent, {
            direction: this.dir.value,
            data: {
              col,
              values: this.search.find(
                (s) => s.name === 'search' + capitalizedColName
              )?.values,
              autoComplete: [
                ...new Set(
                  this.data?.results.map((r) =>
                    typeof r === 'object' && r !== null && col.name in r
                      ? String((r as Record<string, unknown>)[col.name])
                      : undefined
                  )
                )
              ]
            }
          })
          .afterClosed()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((result: SearchParameter) => {
            if (result) {
              this.updateSearch(result);
            }
          });
        break;
    }
  }

  private updateSearch(parameter: SearchParameter) {
    const found = this.search.find((s) => s.name === parameter.name);
    if (found) {
      found.values = parameter.values;
    } else {
      this.search.push(parameter);
    }
    this.page = 0;
    this.refresh$.next();
  }

  exportPage() {
    if (this.estimateFileSize(false) > 500000000) {
      this.dialog.open(InputDialogComponent, {
        direction: this.dir.value,
        data: {
          title: $localize`File too large`,
          content: $localize`The file is expected to be too large and so the operation was aborted.`,
          showCancel: false,
          showDelete: false,
          showInput: false
        }
      });
      return;
    }
    this.updating = true;
    this.downloadPage()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({ complete: () => (this.updating = false) });
  }

  exportAll() {
    if (this.estimateFileSize(true) > 500000000) {
      this.dialog.open(InputDialogComponent, {
        direction: this.dir.value,
        data: {
          title: $localize`File too large`,
          content: $localize`The file is expected to be too large and so the operation was aborted.`,
          showCancel: false,
          showDelete: false,
          showInput: false
        }
      });
      return;
    }
    this.updating = true;
    this.downloadAll()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({ complete: () => (this.updating = false) });
  }

  clearFilter(col: ColumnDefinition) {
    const srch = 'search' + col.name.toLowerCase();
    this.search = this.search.filter(
      (s) => !s.name.toLowerCase().includes(srch)
    );
    this.refresh$.next();
  }

  onResizeColumn(event: MouseEvent, index: number) {
    if (event.buttons !== 1) {
      return;
    }
    this.currentResizeIndex = index;
    this.pressed = true;
    this.startX = event.pageX;
    this.startWidth = (<HTMLElement>event.target)?.clientWidth;
    event.preventDefault();
    this.moving = true;
    this.mouseMove(index);
  }

  colorLeave(index: number) {
    if (this.moving) {
      return;
    }
    const column = this.colDefinitions[index];
    const columnEls = Array.from(
      document.getElementsByClassName('mat-column-' + column.name)
    );

    columnEls.forEach((el: Element) => {
      const header = el as HTMLDivElement;
      header.style.borderLeftColor = 'initial';
      header.style.borderLeftWidth = 'initial';
      header.style.borderLeftStyle = 'initial';
    });
  }

  colorMove(event: MouseEvent, index: number) {
    if (this.moving) {
      return;
    }
    const column = this.colDefinitions[index];
    const columnEls = Array.from(
      document.getElementsByClassName('mat-column-' + column.name)
    );

    columnEls.forEach((el: Element) => {
      const rect = el.getBoundingClientRect();
      const header = el as HTMLDivElement;
      if (event.clientX >= rect.x - 6 && event.clientX <= rect.x + 6) {
        header.style.borderLeftColor = 'black';
        header.style.borderLeftWidth = 'thin';
        header.style.borderLeftStyle = 'solid';
      } else {
        header.style.borderLeftColor = 'initial';
        header.style.borderLeftWidth = 'initial';
        header.style.borderLeftStyle = 'initial';
      }
    });
  }

  mouseMove(index: number) {
    this.resizableMousemove = this.renderer.listen(
      'document',
      'mousemove',
      (event: MouseEvent) => {
        if (this.pressed && event.buttons) {
          const dx = -event.pageX + this.startX;
          const width = this.startWidth + dx;
          if (this.currentResizeIndex === index && width > 50) {
            this.setColumnWidthChanges(index, width);
          }
        }
      }
    );
    this.resizableMouseup = this.renderer.listen('document', 'mouseup', () => {
      if (this.pressed) {
        this.pressed = false;
        this.moving = false;
        this.colorLeave(this.currentResizeIndex);
        this.currentResizeIndex = -1;
        this.resizableMousemove();
        this.resizableMouseup();
      }
    });
  }

  setColumnWidthChanges(index: number, width: number) {
    const orgWidth = this.colDefinitions[index].width;
    const dx = width - orgWidth;
    if (dx !== 0) {
      // const j = index - 1;
      // const newWidth = this.colDefinitions[j].width - dx;
      if (width > 50) {
        this.colDefinitions[index].width = width;
        this.setColumnWidth(this.colDefinitions[index]);
        // this.colDefinitions[j].width = newWidth;
        // this.setColumnWidth(this.colDefinitions[j]);
      }
    }
  }

  setColumnWidth(column: ColumnDefinition) {
    const columnEls = Array.from(
      document.getElementsByClassName('mat-column-' + column.name)
    );
    columnEls.forEach((el: Element) => {
      (el as HTMLDivElement).style.maxWidth = column.width + 'px';
      (el as HTMLDivElement).style.width = column.width + 'px';
    });
  }

  estimateFileSize(all: boolean) {
    if (!this.data || !this.data.results || this.data.results.length === 0) {
      return 0;
    }

    const size = this.roughSizeOfObject(this.data.results[0]);
    if (all) {
      return size * this.data.total;
    } else {
      return size * this.data.results.length;
    }
  }

  roughSizeOfObject(object: unknown): number {
    const objectList: unknown[] = [];
    const stack: unknown[] = [object];
    let bytes = 0;

    while (stack.length) {
      const value = stack.pop();

      if (typeof value === 'boolean') {
        bytes += 4;
      } else if (typeof value === 'string') {
        bytes += value.length * 2;
      } else if (typeof value === 'number') {
        bytes += 8;
      } else if (
        typeof value === 'object' &&
        value !== null &&
        !objectList.includes(value)
      ) {
        objectList.push(value);

        if (typeof value === 'object' && value !== null) {
          for (const i in value as Record<string, unknown>) {
            if (Object.prototype.hasOwnProperty.call(value, i)) {
              stack.push((value as Record<string, unknown>)[i]);
            }
          }
        }
      }
    }
    return bytes;
  }
}
